// web/src/components/RankBadge.tsx
import React from "react";
import { getDisplayRankTitle, isGrandArcanist } from "@/shared/ranks";

type Size = "small" | "normal" | "large";

export type RankBadgeUser = {
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
};

type RankBadgeProps = {
  // New points-based API (preferred)
  points?: number | null;
  title?: string | null;         // explicit title override
  uid?: string | null;           // subject user's uid (no auth fallback)
  username?: string | null;      // subject user's username

  // Back-compat props from the older component
  rank?: string | null;
  size?: Size;
  showTitle?: boolean;
  user?: RankBadgeUser;
  userChip?: React.ReactNode;    // if provided, shown instead of minimal avatar
  showName?: boolean;            // show @username next to avatar/chip
  className?: string;
  showPoints?: boolean;

  // allow extra shape so we can read from callers that pass profile/user
} & Record<string, any>;

function coerceNum(v: any): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function resolvePointsFromAny(obj: any): number | null {
  if (!obj || typeof obj !== "object") return null;
  const cand =
    coerceNum(obj.points) ??
    coerceNum(obj.rank_points_total) ??
    coerceNum(obj.rankPointsTotal) ??
    coerceNum(obj.total_score) ??
    coerceNum(obj.totalScore) ??
    coerceNum(obj.score) ??
    coerceNum(obj.stats?.rank_points_total) ??
    coerceNum(obj.profile?.rank_points_total) ??
    coerceNum(obj.profile?.total_score) ??
    coerceNum(obj.user?.rank_points_total) ??
    coerceNum(obj.user?.total_score) ??
    null;
  return cand;
}

export default function RankBadge(props: RankBadgeProps) {
  const size = (props.size as Size) ?? "normal";
  const showTitle = props.showTitle ?? true;
  const showPoints = props.showPoints ?? true;
  const showName = props.showName ?? false;

  // Points: explicit -> derive from common shapes -> 0
  const derived =
    resolvePointsFromAny(props) ??
    resolvePointsFromAny(props.profile) ??
    resolvePointsFromAny(props.user) ??
    resolvePointsFromAny(props.owner) ??
    resolvePointsFromAny(props.data) ??
    0;
  const points = Math.max(0, props.points != null ? Number(props.points) : derived);

  // Subject identity (no auth fallback so GA doesn’t bleed onto others)
  const uidHint = props.uid ?? null;
  const usernameHint = props.username ?? props.user?.username ?? props.user?.displayName ?? null;

  // Title: explicit props.title -> legacy props.rank -> computed from points (with GA override)
  const computedTitle = getDisplayRankTitle(points, { uid: uidHint, username: usernameHint });
  const title =
    (typeof props.title === "string" && props.title) ||
    (typeof props.rank === "string" && props.rank) ||
    computedTitle;

  // Visual label (optionally abbreviated)
  const displayText = showTitle ? title : String(title).charAt(0).toUpperCase();

  // Size presets + layout helpers (from legacy component)
  const avatarClasses: Record<Size, string> = {
    small: "w-5 h-5 text-[10px]",
    normal: "w-6 h-6 text-[11px]",
    large: "w-7 h-7 text-[12px]",
  };
  const gapClasses: Record<Size, string> = {
    small: "gap-2",
    normal: "gap-2",
    large: "gap-2.5",
  };

  // Scroll sizing
  const dims =
    size === "large"
      ? { text: 16, padX: 18, padY: 10, minW: 160, maxW: 480, extraEdge: 6 }
      : size === "small"
      ? { text: 12, padX: 12, padY: 6, minW: 110, maxW: 320, extraEdge: 5 }
      : { text: 14, padX: 14, padY: 8, minW: 130, maxW: 360, extraEdge: 5 };

  const measureRef = React.useRef<HTMLSpanElement | null>(null);
  const [textW, setTextW] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    setTextW(Math.ceil(el.getBoundingClientRect().width));
  }, [displayText, size, dims.text]);

  const width = Math.max(dims.minW, Math.min(dims.maxW, textW + dims.padX * 5));
  const height = Math.round(dims.text + dims.padY * 6 + dims.extraEdge);

  // Colors
  const isGrand = isGrandArcanist({ uid: uidHint, username: usernameHint });
  const textColor = isGrand ? "#ef4444" : "#000000";
  const textShadow = isGrand
    ? "0 0 8px rgba(251,191,36,.75), 0 0 12px rgba(245,158,11,.65), 0 1px 0 rgba(0,0,0,.55)"
    : "0 0 8px rgba(251,191,36,.75), 0 0 12px rgba(245,158,11,.55), 0 1px 0 rgba(0,0,0,.5)";

  // Name display
  const subjectName = usernameHint ?? props.user?.username ?? props.user?.displayName ?? undefined;
  const initials = (subjectName ?? "U").slice(0, 2).toUpperCase();

  // If no user or chip is provided, render just the scroll rank + points (new style)
  if (!props.user && !props.userChip) {
    return (
      <div className={["inline-flex items-center gap-2", props.className || ""].join(" ")}>
        {/* measurer */}
        <span
          ref={measureRef}
          className="absolute opacity-0 pointer-events-none whitespace-nowrap"
          style={{ fontSize: `${dims.text}px`, fontWeight: 700, letterSpacing: ".2px" }}
          aria-hidden="true"
        >
          {displayText}
        </span>

        {/* scroll */}
        <div
          className="relative inline-flex items-center justify-center select-none"
          style={{
            width,
            height,
            backgroundImage: "url(/images/scroll.png)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 100%",
          }}
          aria-label={`Rank ${title}`}
          title={title || ""}
        >
          <span
            className="whitespace-nowrap"
            style={{
              fontSize: `${dims.text}px`,
              fontWeight: 700,
              color: textColor,
              textShadow,
              padding: `0 ${dims.padX}px`,
              letterSpacing: ".2px",
              lineHeight: 1,
            }}
          >
            {displayText}
          </span>
        </div>

        {showPoints && (
          <span className={["text-slate-300", size === "small" ? "text-[10px]" : "text-xs"].join(" ")}>
            Pts {points}
          </span>
        )}
      </div>
    );
  }

  // Cohesive pill: chip/avatar first, then scroll rank, optional @name (restored legacy UX)
  return (
    <span
      className={[
        "inline-flex items-center rounded-full whitespace-nowrap",
        gapClasses[size],
        props.className || "",
      ].join(" ")}
    >
      {/* User chip (preferred) or minimal avatar */}
      {props.userChip ? (
        <span className="inline-flex items-center shrink-0">{props.userChip}</span>
      ) : (
        <span
          className={[
            "shrink-0 rounded-full overflow-hidden border border-white/15 bg-white/10 grid place-items-center",
            avatarClasses[size],
          ].join(" ")}
        >
          {props.user?.avatarUrl ? (
            <img
              src={props.user.avatarUrl}
              alt={subjectName ? `@${subjectName}` : "User"}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="font-semibold text-white/80">{initials}</span>
          )}
        </span>
      )}

      {/* measurer for scroll */}
      <span
        ref={measureRef}
        className="absolute opacity-0 pointer-events-none whitespace-nowrap"
        style={{ fontSize: `${dims.text}px`, fontWeight: 700, letterSpacing: ".2px" }}
        aria-hidden="true"
      >
        {displayText}
      </span>

      {/* scroll rank */}
      <span
        className="relative inline-flex items-center justify-center select-none"
        style={{
          width,
          height,
          backgroundImage: "url(/images/scroll.png)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
        }}
        title={showTitle ? undefined : String(title)}
      >
        <span
          className="whitespace-nowrap"
          style={{
            fontSize: `${dims.text}px`,
            fontWeight: 700,
            color: textColor,
            textShadow,
            padding: `0 ${dims.padX}px`,
            letterSpacing: ".2px",
            lineHeight: 1,
          }}
        >
          {displayText}
        </span>
      </span>

      {/* Optional @name (restored) */}
      {showName && subjectName && (
        <span
          className={
            {
              small: "text-[11px] max-w-[100px]",
              normal: "text-xs max-w-[140px]",
              large: "text-sm max-w-[180px]",
            }[size] + " truncate opacity-90"
          }
          title={`@${subjectName}`}
        >
          @{subjectName}
        </span>
      )}

      {/* Points (kept) */}
      {showPoints && (
        <span className={["text-slate-300", size === "small" ? "text-[10px]" : "text-xs"].join(" ")}>
          Pts {points}
        </span>
      )}
    </span>
  );
}

// example usage (other user's profile)
// <RankBadge
//   points={profile.points}
//   uid={profile.uid}               // pass the subject user's uid
//   username={profile.username}
// />
